import {
  Box, Card, CardContent, CardHeader, Chip, Paper,
  Stack, Typography, IconButton, Button,
  Snackbar, Alert, ToggleButtonGroup, ToggleButton,
  Divider
} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import HotelIcon from '@mui/icons-material/Hotel';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import PeopleIcon from '@mui/icons-material/People';
import EuroIcon from '@mui/icons-material/Euro';
import dayjs from "dayjs";
import { VacationType, FirstDay, TeamLabel } from '../../shares/Tools';
import { useParams } from 'react-router';
import NumberSpinner from '../../shares/NumberSpinner';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useUpdateTeamMutation, useDeleteTeamMutation, useUpdateTeamStatusMutation } from '../../store/team/TeamApi';
import React from "react";
import { ConfirmDialog } from "../../shares/ConfirmDialog";
import { myConstants } from "../../shares/Constants";
import { CheckCircleOutlined, RemoveCircleOutlined } from "@mui/icons-material";


/*
 * Die Farbe der Teams richtet sich
 * nach dem Status der Anmeldung
 */ 
const STATUS_COLOR = 
{
  registered: 'success',
  waitlist: 'warning',
  cancelled: 'default', 
};


/*
 * Bezeichnung des aktuellen Status
 * eines Teams. 
 */
const STATUS_LABEL = 
{
  registered: 'Angemeldet',
  waitlist: 'Warteliste',
  cancelled: 'Zurückgezogen', 
};


/*
 * Eine Section Card für jeden Teilbereich des Teams, 
 * das man gerne bearbeiten möchten
 */ 
function SectionCard({ icon, title, isEditing, onEdit, onSave, onCancel, isLoading, children }) {
  return (
    <Paper elevation={0} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 2 }}>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        px: 2, py: 1, bgcolor: 'grey.50',
        borderBottom: '1px solid', borderColor: 'divider',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
          {icon}
          <Typography variant="subtitle2" fontWeight={600}>{title}</Typography>
        </Box>
        {!isEditing ? (
          <Button size="small" startIcon={<EditIcon />} onClick={onEdit}>
            Ändern
          </Button>
        ) : (
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={onSave} disabled={isLoading}>
              Speichern
            </Button>
            <Button size="small" variant="outlined" startIcon={<CloseIcon />} onClick={onCancel}>
              Abbrechen
            </Button>
          </Stack>
        )}
      </Box>
      <Box sx={{ p: 2, '& .MuiFormControl-root': { width: '100%' } }}>
        {children}
      </Box>
    </Paper>
  );
};


function InfoRow({ label, value }) 
{
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.6,
      '&:not(:last-child)': { borderBottom: '1px solid', borderColor: 'divider' }
    }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={600}>{value}</Typography>
    </Box>
  );
}




export default function TeamDetail({ team, club }) 
{

  const { year } = useParams();
  const [editingSection, setEditingSection] = React.useState(null);
  // Kennzeichen, ob der Lösch-Bestätigungsdialog offen ist oder nicht
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState(false);

  const [updateTeam, { isLoading : isUpdating }] = useUpdateTeamMutation();
  const [deleteTeam, { isLoading : isDeleting }] = useDeleteTeamMutation();
  const [updateTeamStatus] = useUpdateTeamStatusMutation();

  const [form, setForm] = React.useState({});
  // Vereinfachtest setzen der Werte (Feld => Wert) in der
  // Teamstruktur
  const set = (field) => (value) => setForm((content) => ({ ...content, [field]: value }));

  // sicherstellen, dass wir auch wirklich ein Team haben
  if (!team){
    return null;
  } 

  // Bearbeiten des Löschens (sehr vereinfacht,
  // da das meiste direkt in dem Dialog durchgeführt
  // wird):
  const teamStatus        = team.status ?? myConstants.teamModel.status.registered;
  const isOvernight       = team.vacationType === myConstants.overnight;
  const isFirstDayFriday  = team.firstDay === myConstants.fd_friday;
  const hasFood           = team.saturdayBreakfast > 0 || team.saturdayLunch > 0 || team.sundayBreakfast > 0;
  const hasShuttle        = team.shuttleArrivingCount > 0 || team.shuttleDepartingCount > 0;
  //const handleDelete  = () => setConfirmOpen(true);

  // Jeweils vor dem Editieren setzen wir die Sektion
  // fest und übernehmen die entsprechenden Daten
  const startEditing = (section) =>     
  {
    // Setzen der akuellen Daten
    setForm(
      {
        players:                    team.players,
        fellows:                    team.fellows,
        vacationType:               team.vacationType,
        firstDay:                   team.firstDay,
        saturdayBreakfast:          team.saturdayBreakfast,
        sundayBreakfast:            team.sundayBreakfast,
        saturdayLunch:              team.saturdayLunch,
        barSets:                    team.barSets,
        shuttleArrivalPassengers:   team.shuttleArrivingCount,
        shuttleDeparturePassengers: team.shuttleDepartingCount,
        shuttleArrivalDate:         dayjs(team.shuttleArrivingDate),
        shuttleDepartureDate:       dayjs(team.shuttleDepartingDate),
      }
    );
    // Vorbereitungen der Sektion
    setEditingSection(section);
  };


  /*
   * Behandlung des Zurückziehens eines Teams.
  */
  const handleWithdraw = async () => 
  {
    try 
    {
      await updateTeamStatus(
        {
          year, 
          clubLabel:  team.clubLabel,
          teamLabel:  team.label,
          teamCounter:team.counter,
          status:     myConstants.teamModel.status.withdrawn,
        }
      ).unwrap();
    }
    catch (error)
    {
      console.error('Fehler beim Zurückziehen ', error);
    }
  };

  /*
   * Behandlung des Reaktivieren eines Teams.
  */
  const handleReactivate = async () => 
  {
    try 
    {
      await updateTeamStatus(
        {
          year, 
          clubLabel:  team.clubLabel,
          teamLabel:  team.label,
          teamCounter:team.counter,
          status:     myConstants.teamModel.status.registered,
        }
      ).unwrap();
    }
    catch (error)
    {
      if (error.status === 409)
      {
        setErrorMsg(error.data?.error ?? 'Kapazität ist ausgeschöpft.');
      }
      else 
      {
        console.error('Fehler beim Reaktivieren ', error);
      }
    }
  };



  // Bearbeiten des Updates
  const handleSave = async () =>
  {
    try
    {
      await updateTeam(
        {
          year, 
          clubLabel             : team.clubLabel,
          teamLabel             : team.label,
          teamCounter           : team.counter, 
          players               : parseInt(form.players),
          fellows               : parseInt(form.fellows),
          vacationType          : form.vacationType,
          firstDay              : form.firstDay,
          saturdayBreakfast     : parseInt(form.saturdayBreakfast),
          sundayBreakfast       : parseInt(form.sundayBreakfast),
          fridayLunch           : 0,
          saturdayLunch         : parseInt(form.saturdayLunch),
          barSets               : parseInt(form.barSets),
          shuttleArrivingCount  : parseInt(form.shuttleArrivalPassengers),
          shuttleArrivingDate   : form.shuttleArrivalDate.toISOString(),
          shuttleDepartingCount : parseInt(form.shuttleDeparturePassengers),
          shuttleDepartingDate  : form.shuttleDepartureDate.toISOString(),
        }
      ).unwrap();
      setEditingSection(null);
    }
    catch (error)
    {
      console.error('Fehler beim Aktualisieren des Teams: ', error);
    }
  };




 

  
   return (
    <Card id={`team-${team.id}`} sx={{ mb: 2, scrollMarginTop: '80px' }} variant="outlined">
      <CardHeader
        title={TeamLabel(team.label, team.counter)}
        subheader={VacationType(team.vacationType)}
        titleTypographyProps={{ variant: 'h5' }}
        action={
          <>
            <Chip
              size="small" label={STATUS_LABEL[teamStatus]}
              color={STATUS_COLOR[teamStatus]} sx={{ mr: 1, mt: 0.5 }}
            />
            {teamStatus !== myConstants.teamModel.status.withdrawn ? (
              <IconButton onClick={handleWithdraw} title="Zurückziehen" color="default">
                <RemoveCircleOutlined />
              </IconButton>
            ) : (
              <IconButton onClick={handleReactivate} title="Reaktivieren" color="success">
                <CheckCircleOutlined />
              </IconButton>
            )}
            {teamStatus === myConstants.teamModel.status.withdrawn && (
              <IconButton onClick={() => setConfirmOpen(true)} disabled={isDeleting}>
                <DeleteIcon />
              </IconButton>
            )}
          </>
        }
      />

      <CardContent>

        {/* Personen */}
        <SectionCard
          icon={<PeopleIcon fontSize="small" />} title="Personen"
          isEditing={editingSection === 'personen'}
          onEdit={() => startEditing('personen')}
          onSave={handleSave} onCancel={() => setEditingSection(null)}
          isLoading={isUpdating}
        >
          {editingSection === 'personen' ? (
            <Stack spacing={2}>
              <NumberSpinner label="Anzahl SpielerInnen" min={4} size="small"
                value={form.players} onValueChange={set('players')} />
              <NumberSpinner label="Anzahl Mitreisende" size="small"
                value={form.fellows} onValueChange={set('fellows')} />
            </Stack>
          ) : (
            <Box>
              <InfoRow label="SpielerInnen" value={team.players} />
              <InfoRow label="Mitreisende"  value={team.fellows} />
            </Box>
          )}
        </SectionCard>

        {/* Unterkunft */}
        <SectionCard
          icon={<HotelIcon fontSize="small" />} title="Unterkunft & Anreise"
          isEditing={editingSection === 'unterkunft'}
          onEdit={() => startEditing('unterkunft')}
          onSave={handleSave} onCancel={() => setEditingSection(null)}
          isLoading={isUpdating}
        >
          {editingSection === 'unterkunft' ? (
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">Art des Besuchs</Typography>
                <ToggleButtonGroup value={form.vacationType} exclusive fullWidth
                  onChange={(e) => set('vacationType')(e.target.value)} sx={{ mt: 0.5 }}>
                  <ToggleButton value={myConstants.saturday}>Tagesgäste</ToggleButton>
                  <ToggleButton value={myConstants.weekend}>Wochenende</ToggleButton>
                  <ToggleButton value={myConstants.overnight}>Übernachtung</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              {form.vacationType === myConstants.overnight && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Anreisetag</Typography>
                  <ToggleButtonGroup value={form.firstDay} exclusive fullWidth
                    onChange={(e) => set('firstDay')(e.target.value)} sx={{ mt: 0.5 }}>
                    <ToggleButton value={myConstants.fd_friday}>Freitagabend</ToggleButton>
                    <ToggleButton value={myConstants.fd_saturday}>Samstagmorgen</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              )}
            </Stack>
          ) : (
            <Box>
              <InfoRow label="Unterkunft" value={VacationType(team.vacationType)} />
              {isOvernight && <InfoRow label="Anreisetag" value={FirstDay(team.firstDay)} />}
            </Box>
          )}
        </SectionCard>

        {/* Verpflegung */}
        <SectionCard
          icon={<RestaurantIcon fontSize="small" />} title="Verpflegung"
          isEditing={editingSection === 'verpflegung'}
          onEdit={() => startEditing('verpflegung')}
          onSave={handleSave} onCancel={() => setEditingSection(null)}
          isLoading={isUpdating}
        >
          {editingSection === 'verpflegung' ? (
            <Stack spacing={2}>
              {isOvernight && isFirstDayFriday && (
                <NumberSpinner label="Frühstück Samstag" min={0} size="small"
                  value={form.saturdayBreakfast} onValueChange={set('saturdayBreakfast')} />
              )}
              {isOvernight && (
                <NumberSpinner label="Frühstück Sonntag" min={0} size="small"
                  value={form.sundayBreakfast} onValueChange={set('sundayBreakfast')} />
              )}
              <NumberSpinner label="Abendessen Samstag" min={0} size="small"
                value={form.saturdayLunch} onValueChange={set('saturdayLunch')} />
            </Stack>
          ) : hasFood ? (
            <Box>
              {team.saturdayBreakfast > 0 && <InfoRow label="Frühstück Samstag" value={team.saturdayBreakfast} />}
              {team.sundayBreakfast   > 0 && <InfoRow label="Frühstück Sonntag" value={team.sundayBreakfast} />}
              {team.saturdayLunch     > 0 && <InfoRow label="Abendessen Samstag" value={team.saturdayLunch} />}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">Keine Verpflegung gebucht</Typography>
          )}
        </SectionCard>

        {/* Services */}
        <SectionCard
          icon={<DirectionsBusIcon fontSize="small" />} title="Services"
          isEditing={editingSection === 'services'}
          onEdit={() => startEditing('services')}
          onSave={handleSave} onCancel={() => setEditingSection(null)}
          isLoading={isUpdating}
        >
          {editingSection === 'services' ? (
            <Stack spacing={2}>
              <NumberSpinner label="Biergarnituren" size="small"
                value={form.barSets} onValueChange={set('barSets')} />
              <NumberSpinner label="Shuttleplätze Ankunft" size="small"
                value={form.shuttleArrivalPassengers} onValueChange={set('shuttleArrivalPassengers')} />
              <DateTimePicker label="Ankunft am Bahnhof" value={form.shuttleArrivalDate}
                onChange={set('shuttleArrivalDate')} disabled={form.shuttleArrivalPassengers === 0} />
              <NumberSpinner label="Shuttleplätze Abfahrt" size="small"
                value={form.shuttleDeparturePassengers} onValueChange={set('shuttleDeparturePassengers')} />
              <DateTimePicker label="Abfahrt am Bahnhof" value={form.shuttleDepartureDate}
                onChange={set('shuttleDepartureDate')} disabled={form.shuttleDeparturePassengers === 0} />
            </Stack>
          ) : (
            <Box>
              {team.barSets > 0 && (
                <InfoRow label="Biergarnituren" value={`${team.barSets} Garnitur${team.barSets !== 1 ? 'en' : ''}`} />
              )}
              {team.shuttleArrivingCount > 0 && (
                <InfoRow label="Shuttle Ankunft"
                  value={`${team.shuttleArrivingCount} Person${team.shuttleArrivingCount !== 1 ? 'en' : ''} · ${dayjs(team.shuttleArrivingDate).format('DD.MM.YYYY HH:mm')} Uhr`} />
              )}
              {team.shuttleDepartingCount > 0 && (
                <InfoRow label="Shuttle Abfahrt"
                  value={`${team.shuttleDepartingCount} Person${team.shuttleDepartingCount !== 1 ? 'en' : ''} · ${dayjs(team.shuttleDepartingDate).format('DD.MM.YYYY HH:mm')} Uhr`} />
              )}
              {team.barSets === 0 && !hasShuttle && (
                <Typography variant="body2" color="text.secondary">Keine Services gebucht</Typography>
              )}
            </Box>
          )}
        </SectionCard>

        {/* Zahlung (nur Anzeige) */}
        <SectionCard icon={<EuroIcon fontSize="small" />} title="Zahlung">
          <Box>
            <InfoRow label="Turnierbeitrag" value={`EUR ${parseFloat(team.vacationFee).toFixed(2)}`} />
            {team.saturdayBreakfastTotal > 0 && (
              <InfoRow label={`Frühstück Sa. (${team.saturdayBreakfast}×)`} value={`EUR ${parseFloat(team.saturdayBreakfastTotal).toFixed(2)}`} />
            )}
            {team.sundayBreakfastTotal > 0 && (
              <InfoRow label={`Frühstück So. (${team.sundayBreakfast}×)`}   value={`EUR ${parseFloat(team.sundayBreakfastTotal).toFixed(2)}`} />
            )}
            {team.fridayLunchTotal > 0 && (
              <InfoRow label={`Abendessen Fr. (${team.fridayLunch}×)`}       value={`EUR ${parseFloat(team.fridayLunchTotal).toFixed(2)}`} />
            )}
            {team.saturdayLunchTotal > 0 && (
              <InfoRow label={`Abendessen Sa. (${team.saturdayLunch}×)`}     value={`EUR ${parseFloat(team.saturdayLunchTotal).toFixed(2)}`} />
            )}
            {team.barSetTotal > 0 && (
              <InfoRow label={`Biergarnituren (${team.barSets}×)`}           value={`EUR ${parseFloat(team.barSetTotal).toFixed(2)}`} />
            )}
            <Divider sx={{ my: 0.5 }} />
            <InfoRow label="Fälliger Betrag" value={`EUR ${parseFloat(team.vacationFee).toFixed(2)}`} />
            <InfoRow label="Bezahlt"         value={`EUR ${parseFloat(team.feePayedSum).toFixed(2)}`} />
            {dayjs(team.feePayedDate).year() > 1 && (
              <InfoRow label="Zahlungsdatum" value={dayjs(team.feePayedDate).format('DD.MM.YYYY')} />
            )}
          </Box>
        </SectionCard>

      </CardContent>

      <ConfirmDialog
        open={confirmOpen} title="Team löschen"
        text={`${TeamLabel(team.label, team.counter)} wirklich permanent löschen?`}
        onConfirm={async () => {
          try {
            await deleteTeam({ year, clubLabel: team.clubLabel, teamLabel: team.label, teamCounter: team.counter }).unwrap();
          } catch (error) { console.error(error); }
          setConfirmOpen(false);
        }}
        onCancel={() => setConfirmOpen(false)}
      />

      <Snackbar open={!!errorMsg} autoHideDuration={6000} onClose={() => setErrorMsg('')}>
        <Alert severity="error" onClose={() => setErrorMsg('')} variant="filled">{errorMsg}</Alert>
      </Snackbar>
    </Card>
  );
}